import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { getInterclubFeatureToggles, updateInterclubFeatureToggles } from '../services/interclubFeatureToggleService';
import { API_BASE_URL } from '../config/environment';

export default function AdminSettings() {
  const { 
    showQualifierLink, 
    showAdminLink, 
    toggleQualifierLink, 
    toggleAdminLink,
    togglesLoading,
    refreshFeatureToggles,
    debugFeatureToggles
  } = useAuth();
  const { adminStatusLoading, getIdToken, isInterclubAdmin, getInterclubAdminIds } = useFirebaseAuth();
  const [interclubs, setInterclubs] = useState([]);
  const [selectedInterclubId, setSelectedInterclubId] = useState('');
  const [interclubToggles, setInterclubToggles] = useState({
    enableSeasonFeePayments: true,
    enableQualifiers: true,
  });
  const [interclubLoading, setInterclubLoading] = useState(false);
  const [interclubError, setInterclubError] = useState(null);
  const interclubOptions = useMemo(() => {
    if (isInterclubAdmin()) {
      const ids = getInterclubAdminIds();
      return interclubs.filter((interclub) => ids.includes(interclub.id));
    }
    return interclubs;
  }, [interclubs, isInterclubAdmin, getInterclubAdminIds]);

  useEffect(() => {
    const loadInterclubs = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/interclubs`);
        const data = await response.json();
        setInterclubs(data || []);
      } catch (error) {
        console.error('Failed to load interclubs:', error);
      }
    };
    loadInterclubs();
  }, []);

  useEffect(() => {
    if (adminStatusLoading) return;
    if (isInterclubAdmin()) {
      const ids = getInterclubAdminIds();
      if (ids.length > 0) {
        setSelectedInterclubId(ids[0]);
      }
    }
  }, [adminStatusLoading, isInterclubAdmin, getInterclubAdminIds]);

  useEffect(() => {
    const loadInterclubToggles = async () => {
      if (!selectedInterclubId) return;
      try {
        setInterclubLoading(true);
        setInterclubError(null);
        const token = await getIdToken(true);
        const data = await getInterclubFeatureToggles(selectedInterclubId, token);
        setInterclubToggles({
          enableSeasonFeePayments: data.enableSeasonFeePayments !== false,
          enableQualifiers: data.enableQualifiers !== false,
        });
      } catch (error) {
        console.error('Failed to load interclub toggles:', error);
        setInterclubError('Failed to load interclub feature toggles.');
      } finally {
        setInterclubLoading(false);
      }
    };
    loadInterclubToggles();
  }, [selectedInterclubId, getIdToken]);

  const handleToggleChange = async (field) => {
    try {
      if (!selectedInterclubId) return;
      const token = await getIdToken(true);
      const nextValue = !interclubToggles[field];
      const updated = await updateInterclubFeatureToggles(
        selectedInterclubId,
        { [field]: nextValue },
        token
      );
      setInterclubToggles({
        enableSeasonFeePayments: updated.enableSeasonFeePayments !== false,
        enableQualifiers: updated.enableQualifiers !== false,
      });
    } catch (error) {
      console.error('Failed to update interclub toggles:', error);
      setInterclubError('Failed to update interclub feature toggles.');
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          ⚙️ System Settings
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showQualifierLink}
              onChange={toggleQualifierLink}
              color="primary"
              disabled={togglesLoading}
            />
          }
          label={`Show Qualifier Link on Home Page ${togglesLoading ? '(Loading...)' : ''}`}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          When enabled, users will see a link to view current qualifiers on the home page.
        </Typography>
        
        <Divider sx={{ my: 1 }} />
        
        <FormControlLabel
          control={
            <Switch
              checked={showAdminLink}
              onChange={toggleAdminLink}
              color="primary"
              disabled={togglesLoading}
            />
          }
          label={`Show Admin Link in Navigation ${togglesLoading ? '(Loading...)' : ''}`}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          When enabled, admin users will see an admin link in the navigation menu.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>Note:</strong> These changes take effect immediately for all users and are stored in the cloud.
      </Alert>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
        Interclub Feature Toggles
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Control features for a specific interclub.
      </Typography>

      {interclubError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {interclubError}
        </Alert>
      )}

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Interclub</InputLabel>
        <Select
          value={selectedInterclubId}
          label="Interclub"
          onChange={(e) => setSelectedInterclubId(e.target.value)}
          disabled={isInterclubAdmin()}
        >
          <MenuItem value="">
            <em>Select an interclub...</em>
          </MenuItem>
          {interclubOptions.map((interclub) => (
            <MenuItem key={interclub.id} value={interclub.id}>
              {interclub.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {interclubLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={interclubToggles.enableSeasonFeePayments}
                onChange={() => handleToggleChange('enableSeasonFeePayments')}
                color="primary"
                disabled={!selectedInterclubId}
              />
            }
            label="Enable season entry fee payments"
          />
          <FormControlLabel
            control={
              <Switch
                checked={interclubToggles.enableQualifiers}
                onChange={() => handleToggleChange('enableQualifiers')}
                color="primary"
                disabled={!selectedInterclubId}
              />
            }
            label="Enable qualifiers visibility"
          />
        </Box>
      )}
      
      <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={refreshFeatureToggles}
          disabled={togglesLoading}
        >
          Refresh Feature Toggles
        </Button>
        
        <Button
          variant="outlined"
          color="warning"
          onClick={debugFeatureToggles}
        >
          Debug State
        </Button>
      </Box>

    </Paper>
  );
}
